import type { ActionStatus } from '../services/actionService';
import { ensureIshikawa } from './causeDraft';
import type { RootCauseAnalysis } from '../types/issue';

/** Moedas aceitas pelo formato do banco. Não há moeda padrão da organização neste contrato. */
export const COUNTERMEASURE_CURRENCIES = ['BRL', 'USD', 'EUR'] as const;

export interface FiveW2HFields {
  title: string | null;
  description: string | null;
  assignedTo: string | null;
  dueDate: string | null;
  whereText: string | null;
  howText: string | null;
  costAmount: number | null;
}

export interface FiveW2HCompleteness {
  filled: number;
  total: 7;
  label: string;
}

function filled(value: string | null | undefined): boolean {
  return Boolean(value?.trim());
}

/** Qualidade do plano. Não altera status da ação, do problema, da causa nem da eficácia. */
export function fiveW2HCompleteness(fields: FiveW2HFields): FiveW2HCompleteness {
  const filledCount = [
    filled(fields.title),
    filled(fields.description),
    filled(fields.assignedTo),
    filled(fields.dueDate),
    filled(fields.whereText),
    filled(fields.howText),
    fields.costAmount != null,
  ].filter(Boolean).length;
  return {
    filled: filledCount,
    total: 7,
    label: filledCount === 7 ? '5W2H completo' : `${filledCount}/7 preenchidos`,
  };
}

export function parseOptionalCost(raw: string): { amount: number | null; error: string | null } {
  const trimmed = raw.trim();
  if (!trimmed) return { amount: null, error: null };
  const normalized = trimmed.replace(',', '.');
  if (!/^\d+(\.\d+)?$/.test(normalized)) {
    return { amount: null, error: 'Informe o custo como número.' };
  }
  return { amount: Number(normalized), error: null };
}

export interface CountermeasureExtensionInput {
  whereText: string;
  howText: string;
  costAmount: string;
  costCurrency: string;
  existing: boolean;
}

export type CountermeasureExtensionDraft =
  | { ok: true; skip: true }
  | {
    ok: true;
    skip: false;
    whereText: string | null;
    howText: string | null;
    costAmount: number | null;
    costCurrency: string | null;
  }
  | { ok: false; error: string };

/** Custo vazio permanece nulo. Valor sem moeda, ou moeda sem valor, não fecha o contrato. */
export function draftCountermeasureExtension(input: CountermeasureExtensionInput): CountermeasureExtensionDraft {
  const whereText = input.whereText.trim() || null;
  const howText = input.howText.trim() || null;
  const cost = parseOptionalCost(input.costAmount);
  if (cost.error) return { ok: false, error: cost.error };
  const currency = input.costCurrency.trim().toUpperCase();
  if (cost.amount == null && currency) {
    return { ok: false, error: 'Informe o valor do custo ou deixe a moeda em branco.' };
  }
  if (cost.amount != null && !/^[A-Z]{3}$/.test(currency)) {
    return { ok: false, error: 'Informe a moeda do custo.' };
  }
  if (!whereText && !howText && cost.amount == null && !input.existing) {
    return { ok: true, skip: true };
  }
  return {
    ok: true,
    skip: false,
    whereText,
    howText,
    costAmount: cost.amount,
    costCurrency: cost.amount == null ? null : currency,
  };
}

export function confirmedHypothesisContext(analysis: RootCauseAnalysis | null | undefined): string[] {
  return ensureIshikawa(analysis?.ishikawa).flatMap((group) => (
    group.causes.flatMap((cause) => (
      typeof cause !== 'string' && cause.status === 'confirmed' && cause.text.trim()
        ? [cause.text.trim()]
        : []
    ))
  ));
}

export function countermeasureSummary(actions: Array<{ status: ActionStatus }>): string {
  const active = actions.filter((action) => action.status !== 'cancelled');
  const count = (status: ActionStatus) => active.filter((action) => action.status === status).length;
  const parts = [`${active.length} ${active.length === 1 ? 'contramedida' : 'contramedidas'}`];
  const running = count('in_progress');
  const pending = count('pending');
  const blocked = count('blocked');
  const completed = count('completed');
  const cancelled = actions.length - active.length;
  if (running > 0) parts.push(`${running} em andamento`);
  if (pending > 0) parts.push(`${pending} ${pending === 1 ? 'pendente' : 'pendentes'}`);
  if (blocked > 0) parts.push(`${blocked} ${blocked === 1 ? 'bloqueada' : 'bloqueadas'}`);
  if (completed > 0) parts.push(`${completed} ${completed === 1 ? 'concluída' : 'concluídas'}`);
  if (cancelled > 0) parts.push(`${cancelled} ${cancelled === 1 ? 'cancelada' : 'canceladas'}`);
  return parts.join(' · ');
}

export function isActiveCountermeasure(status: ActionStatus): boolean {
  return status !== 'cancelled';
}

function countLabel(count: number, singular: string, plural: string): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

/** Resumo operacional. Canceladas entram na lista, fora da contagem ativa. */
export function executionHeadline(actions: Array<{ status: ActionStatus }>): string {
  const active = actions.filter((action) => isActiveCountermeasure(action.status));
  const count = (status: ActionStatus) => active.filter((action) => action.status === status).length;
  const parts = [countLabel(active.length, 'contramedida', 'contramedidas')];
  const completed = count('completed');
  const running = count('in_progress');
  const blocked = count('blocked');
  const pending = count('pending');
  const cancelled = actions.length - active.length;
  if (completed > 0) parts.push(countLabel(completed, 'concluída', 'concluídas'));
  if (running > 0) parts.push(`${running} em execução`);
  if (blocked > 0) parts.push(countLabel(blocked, 'bloqueada', 'bloqueadas'));
  if (pending > 0) parts.push(countLabel(pending, 'pendente', 'pendentes'));
  if (cancelled > 0) parts.push(countLabel(cancelled, 'cancelada', 'canceladas'));
  return parts.join(' · ');
}

/** Concluídas sobre contramedidas não canceladas. Mede execução, não eficácia. */
export function executionProgress(actions: Array<{ status: ActionStatus }>): {
  completed: number;
  active: number;
  percent: number;
} {
  const active = actions.filter((action) => isActiveCountermeasure(action.status));
  const completed = active.filter((action) => action.status === 'completed').length;
  return {
    completed,
    active: active.length,
    percent: active.length === 0 ? 0 : Math.round((completed / active.length) * 100),
  };
}
